/**
 * Theme Generator
 *
 * Generates theme overrides (colors, fonts) for custom branding.
 * Used by ThemeProvider for app-level theming and BrandedPreviewWrapper for scoped theming.
 *
 * ## Color Palette Generation
 *
 * 1. Takes an input hex color (e.g., #dd3333)
 * 2. Generates a full palette of shades (100-950)
 * 3. For dark mode, inverts the shade scale around 600 for proper contrast
 *
 * ### Crossover Pattern (matching colors.ts)
 *
 * In dark mode, lighter shades should appear darker and vice versa,
 * with shade 600 as the pivot point (same for both modes):
 * - Light mode: 100=lightest, 950=darkest, 600=base
 * - Dark mode: 100=darkest, 950=lightest, 600=base (same as light)
 *
 * This maintains proper contrast on dark backgrounds while keeping
 * the primary brand color (600) consistent across modes.
 *
 * ## Font Overrides
 *
 * Generates CSS custom properties for heading and body fonts,
 * allowing branding configs to override the default theme fonts.
 */
import tinycolor from "tinycolor2";
import { ColorPalette } from "@shared/theme/types";
import { getContrastTextColor } from "@shared/theme/utils/contrast";

const BASE_SHADE_LEVEL = 600;

/**
 * Maps shades to their inverted counterparts for dark mode.
 * Shade 600 (the base color) stays the same - it's the pivot point.
 * Shades below 600 map to shades above 600 and vice versa.
 */
const DARK_MODE_SHADE_MAP: Record<number, number> = {
  100: 950,
  200: 900,
  300: 800,
  400: 700,
  500: 500, // Unique intermediate value
  600: 600, // Pivot - same for both modes
  700: 500,
  800: 400,
  900: 300,
  950: 200,
};

/**
 * Alpha values for blending each shade with white (lighter) or black (darker).
 * Shade 600 is the base (alpha = 1), shades below blend with white,
 * shades above blend with black.
 */
const BLEND_MAP: Record<number, number> = {
  100: 0.15,
  200: 0.3,
  300: 0.45,
  400: 0.6,
  500: 0.8,
  600: 1, // Base color - no blending
  700: 0.8,
  800: 0.6,
  900: 0.4,
  950: 0.25,
};

/**
 * Blend a foreground color with a background color at a given alpha.
 */
function blendWithBackground(
  fg: tinycolor.Instance,
  bg: tinycolor.Instance,
  alpha: number
): string {
  const fgRgb = fg.toRgb();
  const bgRgb = bg.toRgb();

  const r = Math.round(fgRgb.r * alpha + bgRgb.r * (1 - alpha));
  const g = Math.round(fgRgb.g * alpha + bgRgb.g * (1 - alpha));
  const b = Math.round(fgRgb.b * alpha + bgRgb.b * (1 - alpha));

  return tinycolor({ r, g, b }).toHexString();
}

/**
 * Generate a single shade from the base color.
 * Shades < 600 blend with white, shades > 600 blend with black.
 */
function generateShade(baseHex: string, shadeLevel: number): string {
  const base = tinycolor(baseHex);
  const isLighter = shadeLevel < BASE_SHADE_LEVEL;
  const bg = tinycolor(isLighter ? "#ffffff" : "#000000");
  const alpha = BLEND_MAP[shadeLevel] ?? 1;

  return blendWithBackground(base, bg, alpha);
}

/**
 * Generate a full color palette with light/dark mode variants.
 *
 * @param hex - The base color (will be placed at shade 600)
 * @returns ColorPalette with { shade: { light, dark } } format
 *
 * @example
 * const palette = colorToPalette("#dd3333");
 * // palette[600].light === "#dd3333"
 * // palette[600].dark === "#dd3333" (same - pivot point)
 * // palette[100].light === very light red
 * // palette[100].dark === very dark red (inverted)
 */
export function colorToPalette(hex: string): ColorPalette {
  const shades = [100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

  return shades.reduce<ColorPalette>((palette, shade) => {
    const invertedShade = DARK_MODE_SHADE_MAP[shade];

    return {
      ...palette,
      [shade]: {
        light: shade === BASE_SHADE_LEVEL ? hex : generateShade(hex, shade),
        dark:
          invertedShade === BASE_SHADE_LEVEL
            ? hex
            : generateShade(hex, invertedShade),
      },
    };
  }, {});
}

/**
 * Validate and normalize a color string to hex format.
 * Returns null if invalid.
 */
export function validateColorString(str: unknown): string | null {
  if (str && typeof str === "string") {
    const color = tinycolor(str);
    if (color.isValid()) {
      return color.toHexString();
    }
  }
  return null;
}

/**
 * Generate CSS custom properties for a color palette.
 * Used for scoped theming (e.g., applying branding only to a specific container).
 *
 * Returns both light and dark mode variables that can be applied via CSS.
 * Mirrors the token structure from generateFullPalette in colors.ts.
 *
 * @param hex - The base color hex value
 * @param colorName - The token name (e.g., "primary", "secondary")
 * @returns Object with lightVars and darkVars CSS custom properties
 */
export function generateScopedColorVars(
  hex: string,
  colorName: string
): { lightVars: Record<string, string>; darkVars: Record<string, string> } {
  const palette = colorToPalette(hex);
  const lightVars: Record<string, string> = {};
  const darkVars: Record<string, string> = {};

  // Chakra v3 uses --chakra-colors-* prefix for CSS variables
  const prefix = "chakra-colors";

  // Numbered shades (100-950)
  Object.entries(palette).forEach(([shade, { light, dark }]) => {
    lightVars[`--${prefix}-${colorName}-${shade}`] = light;
    darkVars[`--${prefix}-${colorName}-${shade}`] = dark;
  });

  // Semantic tokens - must match generateFullPalette in colors.ts
  // solid = 600
  lightVars[`--${prefix}-${colorName}-solid`] = palette[600].light;
  darkVars[`--${prefix}-${colorName}-solid`] = palette[600].dark;

  // contrast (always white for solid buttons)
  lightVars[`--${prefix}-${colorName}-contrast`] = "#FFFFFF";
  darkVars[`--${prefix}-${colorName}-contrast`] = "#FFFFFF";

  // fg = 800 (light), 700 (dark)
  lightVars[`--${prefix}-${colorName}-fg`] = palette[800].light;
  darkVars[`--${prefix}-${colorName}-fg`] = palette[700].dark;

  // bg = 800 (light), 200 (dark)
  lightVars[`--${prefix}-${colorName}-bg`] = palette[800].light;
  darkVars[`--${prefix}-${colorName}-bg`] = palette[200].dark;

  // muted = 100 (light), 900 (dark)
  lightVars[`--${prefix}-${colorName}-muted`] = palette[100].light;
  darkVars[`--${prefix}-${colorName}-muted`] = palette[900].dark;

  // subtle = 300 (light), 950 (dark)
  lightVars[`--${prefix}-${colorName}-subtle`] = palette[300].light;
  darkVars[`--${prefix}-${colorName}-subtle`] = palette[950].dark;

  // emphasized (hover) = 800 (light), 400 (dark)
  lightVars[`--${prefix}-${colorName}-emphasized`] = palette[800].light;
  darkVars[`--${prefix}-${colorName}-emphasized`] = palette[400].dark;

  // pressed = 400 (light), 800 (dark)
  lightVars[`--${prefix}-${colorName}-pressed`] = palette[400].light;
  darkVars[`--${prefix}-${colorName}-pressed`] = palette[800].dark;

  // disabled = 200 (light), 900 (dark)
  lightVars[`--${prefix}-${colorName}-disabled`] = palette[200].light;
  darkVars[`--${prefix}-${colorName}-disabled`] = palette[900].dark;

  // focusRing = 600 (both modes)
  lightVars[`--${prefix}-${colorName}-focus-ring`] = palette[600].light;
  darkVars[`--${prefix}-${colorName}-focus-ring`] = palette[600].dark;

  // Dark button variants
  lightVars[`--${prefix}-${colorName}-solid-dark`] = palette[800].light;
  darkVars[`--${prefix}-${colorName}-solid-dark`] = palette[800].dark;

  lightVars[`--${prefix}-${colorName}-emphasized-dark`] = palette[900].light;
  darkVars[`--${prefix}-${colorName}-emphasized-dark`] = palette[900].dark;

  lightVars[`--${prefix}-${colorName}-pressed-dark`] = palette[700].light;
  darkVars[`--${prefix}-${colorName}-pressed-dark`] = palette[500].dark;

  lightVars[`--${prefix}-${colorName}-disabled-dark`] = palette[500].light;
  darkVars[`--${prefix}-${colorName}-disabled-dark`] = palette[500].dark;

  lightVars[`--${prefix}-${colorName}-contrast-dark`] = "#FFFFFF";
  darkVars[`--${prefix}-${colorName}-contrast-dark`] = palette[200].dark;

  // Auto-contrast text colors (WCAG luminance-based)
  lightVars[`--${prefix}-${colorName}-solid-contrast`] = getContrastTextColor(
    palette[600].light
  );
  darkVars[`--${prefix}-${colorName}-solid-contrast`] = getContrastTextColor(
    palette[600].dark
  );

  lightVars[`--${prefix}-${colorName}-emphasized-contrast`] =
    getContrastTextColor(palette[800].light);
  darkVars[`--${prefix}-${colorName}-emphasized-contrast`] =
    getContrastTextColor(palette[400].dark);

  lightVars[`--${prefix}-${colorName}-pressed-contrast`] = getContrastTextColor(
    palette[400].light
  );
  darkVars[`--${prefix}-${colorName}-pressed-contrast`] = getContrastTextColor(
    palette[800].dark
  );

  lightVars[`--${prefix}-${colorName}-disabled-contrast`] =
    getContrastTextColor(palette[200].light);
  darkVars[`--${prefix}-${colorName}-disabled-contrast`] = getContrastTextColor(
    palette[900].dark
  );

  lightVars[`--${prefix}-${colorName}-solid-dark-contrast`] =
    getContrastTextColor(palette[800].light);
  darkVars[`--${prefix}-${colorName}-solid-dark-contrast`] =
    getContrastTextColor(palette[800].dark);

  lightVars[`--${prefix}-${colorName}-emphasized-dark-contrast`] =
    getContrastTextColor(palette[900].light);
  darkVars[`--${prefix}-${colorName}-emphasized-dark-contrast`] =
    getContrastTextColor(palette[900].dark);

  lightVars[`--${prefix}-${colorName}-pressed-dark-contrast`] =
    getContrastTextColor(palette[700].light);
  darkVars[`--${prefix}-${colorName}-pressed-dark-contrast`] =
    getContrastTextColor(palette[500].dark);

  lightVars[`--${prefix}-${colorName}-disabled-dark-contrast`] =
    getContrastTextColor(palette[500].light);
  darkVars[`--${prefix}-${colorName}-disabled-dark-contrast`] =
    getContrastTextColor(palette[500].dark);

  return { lightVars, darkVars };
}

/**
 * Generate CSS custom properties for font overrides.
 * Used for scoped theming with custom branding fonts.
 *
 * @param headingFont - Font family for headings (optional)
 * @param bodyFont - Font family for body text (optional)
 * @returns CSS custom properties for fonts
 */
export function generateScopedFontVars(
  headingFont?: string | null,
  bodyFont?: string | null
): Record<string, string> {
  const fontVars: Record<string, string> = {};

  // Chakra v3 uses --chakra-fonts-* prefix for font family tokens
  const prefix = "chakra-fonts";

  if (headingFont) {
    // Quote font names that contain spaces
    const quotedHeading = headingFont.includes(" ")
      ? `"${headingFont}"`
      : headingFont;
    fontVars[`--${prefix}-heading`] = `${quotedHeading}, sans-serif`;
  }

  if (bodyFont) {
    // Quote font names that contain spaces
    const quotedBody = bodyFont.includes(" ") ? `"${bodyFont}"` : bodyFont;
    fontVars[`--${prefix}-body`] = `${quotedBody}, sans-serif`;
  }

  return fontVars;
}
